/**
 * Prepare a gasless buy operation using Permit2
 *
 * Single signature flow: User signs once, authorizing both token transfer AND trade
 *
 * Optimized for minimal RPC calls:
 * - Uses parallel reads (batched automatically if client has multicall enabled)
 * - Optionally skips Permit2 allowance check (saves 1 call on subsequent buys)
 * - Optionally includes balance (no extra calls when batched)
 * - Reuses prices from initial fetch for share calculation
 */
import type { Address, PublicClient } from "viem"
import { encodeFunctionData, erc20Abi, maxUint256 } from "viem"
import { readContract } from "viem/actions"
import { breakoutMarketAbi } from "../../abi/generated/breakoutMarket"
import { Errors } from "../../errors"
import { deriveMarketStatus } from "../../internal/contracts"
import {
	createDeadline,
	createDeadlineDate,
	DEFAULT_DEADLINE_SECONDS,
	DEFAULT_SLIPPAGE_BPS,
	fromPriceScale,
	fromUsdcScale,
	toShareScale,
	toUsdcScale,
	validateSlippageBps,
} from "../../internal/convert"
import { computePrice } from "../../internal/lmsr"
import {
	buildPermit2BuySigningPayload,
	generatePermit2Nonce,
	PERMIT2_ADDRESS,
} from "../../internal/signing"
import { calculateSharesForUsd, MIN_USD_AMOUNT } from "../../internal/usdToShares"
import type { BuyPreparation, BuyQuote } from "../../types"

export interface PrepareBuyParams {
	/** Market contract address */
	market: Address
	/** User wallet address */
	user: Address
	/** Outcome bucket index (0-indexed) */
	bucket: number
	/** Amount of USDC to spend (human-readable). The contract calculates optimal shares. */
	usdAmount: number
	/** Chain ID (required - get from your app config, e.g., chainConfig from getChainConfig) */
	chainId: number
	/** USDC token address (required - get from chainConfig.usdc) */
	usdcAddress: Address
	/** Slippage tolerance in basis points (default: 200 = 2%) */
	slippageBps?: number
	/** Deadline in seconds from now (default: 300 = 5 minutes) */
	deadlineSeconds?: number
	/**
	 * Skip Permit2 allowance check.
	 * - true: Skip check (saves 1 RPC call). Use after user has approved Permit2.
	 * - false: Check allowance. needsPermit2Approval will be accurate.
	 */
	skipPermit2Check: boolean
	/**
	 * Include user's USDC balance in result.
	 * - true: Fetch balance in parallel. Result includes userBalance.
	 * - false: Skip balance fetch. userBalance will be undefined.
	 */
	includeBalance: boolean
}

export interface PrepareBuyResult extends BuyPreparation {
	/** True if user needs to approve Permit2 first */
	needsPermit2Approval: boolean
	/** User's USDC balance (only included if includeBalance: true) */
	userBalance?: number
	/** Chain ID used for signing */
	chainId: number
}

/**
 * Prepare a gasless buy operation using Permit2
 *
 * The user specifies how much USDC they want to spend, and the contract calculates
 * the optimal number of shares at execution time. This ensures:
 * - User pays exactly what the shares cost (no overpayment)
 * - Slippage protection via minShares parameter
 *
 * Flow:
 * 1. Check if user has approved Permit2 (one-time, can be silent with embedded wallet)
 * 2. Call prepareBuy() to get signing payload
 * 3. User signs the Permit2 witness payload (single signature)
 * 4. Call executeBuy() with the signature (backend)
 *
 * @example
 * ```ts
 * import { prepareBuy } from "@breakout/sdk/actions/prepare"
 *
 * // User wants to spend $50 USDC
 * const preparation = await prepareBuy(publicClient, {
 *   market: "0x...",
 *   user: "0x...",
 *   bucket: 0,
 *   usdAmount: 50,
 *   chainId: 84532, // From your app config
 *   usdcAddress: "0x...", // From chainConfig.usdc
 *   skipPermit2Check: false, // First buy - check allowance
 *   includeBalance: true, // Get balance in parallel
 * })
 *
 * console.log(preparation.quote.sharesOut) // e.g., 142.5 shares (estimated)
 * console.log(preparation.quote.total)     // e.g., $49.87 (estimated cost)
 *
 * if (preparation.needsPermit2Approval) {
 *   // User needs to approve Permit2 first (one-time)
 * }
 *
 * // User signs preparation.signingPayload
 * // Then call executeBuy with the signature
 * ```
 */
export async function prepareBuy(
	client: PublicClient,
	params: PrepareBuyParams,
): Promise<PrepareBuyResult> {
	const {
		market,
		user,
		bucket,
		usdAmount,
		chainId,
		usdcAddress,
		slippageBps = DEFAULT_SLIPPAGE_BPS,
		deadlineSeconds = DEFAULT_DEADLINE_SECONDS,
		skipPermit2Check,
		includeBalance,
	} = params

	// Validate USD amount (check NaN, Infinity, negative, too small)
	if (!Number.isFinite(usdAmount) || usdAmount <= 0) {
		throw Errors.invalidAmount("USD amount must be a positive number")
	}
	if (usdAmount < MIN_USD_AMOUNT) {
		throw Errors.invalidAmount(`Minimum amount is $${MIN_USD_AMOUNT.toFixed(2)}`)
	}

	// Validate slippage
	validateSlippageBps(slippageBps)

	// =========================================================================
	// Parallel Data Fetching
	// Note: If client has batch.multicall enabled, these are automatically batched
	// =========================================================================

	// Core market data (always needed)
	const marketInfoPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "getMarketInfo",
	})
	const resolutionPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "resolution",
	})
	const pausedAtPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "emergencyPausedAt",
	})
	const bucketCountPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "bucketCount",
	})
	const feeBpsPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "feeBps",
	})
	const pricesPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "getPrices",
	})
	const quantitiesPromise = readContract(client, {
		address: market,
		abi: breakoutMarketAbi,
		functionName: "getAllQuantities",
	})

	// Optional: Permit2 allowance check
	const usdcAmountScaled = toUsdcScale(usdAmount)
	const allowancePromise = skipPermit2Check
		? Promise.resolve(null)
		: readContract(client, {
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "allowance",
				args: [user, PERMIT2_ADDRESS],
			})

	// Optional: USDC balance
	const balancePromise = includeBalance
		? readContract(client, {
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "balanceOf",
				args: [user],
			})
		: Promise.resolve(null)

	// Execute all reads in parallel
	const [
		marketInfo,
		resolution,
		pausedAt,
		bucketCount,
		feeBps,
		rawPrices,
		rawQuantities,
		permit2Allowance,
		rawBalance,
	] = await Promise.all([
		marketInfoPromise,
		resolutionPromise,
		pausedAtPromise,
		bucketCountPromise,
		feeBpsPromise,
		pricesPromise,
		quantitiesPromise,
		allowancePromise,
		balancePromise,
	])

	// Destructure tuple returns from contract (ABI returns tuples for structs)
	// getMarketInfo: [bucketCount, liquidityParam, feeBps, openTime, cutoffTime, status]
	const [, , , openTimeSec, cutoffTimeSec, infoStatus] = marketInfo
	// resolution: [winningOutcome, dataUri, nonce, isResolved, isFinalized]
	const [winningOutcome, dataUri, nonce, isResolved, isFinalized] = resolution

	// =========================================================================
	// Validation and Status Check
	// =========================================================================

	// Validate bucket
	if (bucket < 0 || bucket >= bucketCount) {
		throw Errors.invalidBucket(bucket, bucketCount)
	}

	// Build resolution object for status derivation
	const resolutionObj = { winningOutcome, dataUri, nonce, isResolved, isFinalized }

	// Check market status
	const status = deriveMarketStatus(infoStatus, openTimeSec, cutoffTimeSec, resolutionObj, pausedAt)

	if (status.type === "paused") {
		throw Errors.marketPaused()
	}
	if (status.type === "pending") {
		throw Errors.marketPending(new Date(Date.now() + status.opensIn))
	}
	if (status.type !== "open") {
		throw Errors.marketClosed()
	}

	// =========================================================================
	// Share Calculation (for quote display and minShares calculation)
	// =========================================================================

	// Calculate expected shares for the full budget to show user what they'll get.
	// The contract will recalculate at execution time, but this gives an accurate estimate.
	const shareCalc = await calculateSharesForUsd(
		client,
		market,
		bucket,
		usdAmount,
		Number(feeBps),
		rawPrices,
	)

	if (shareCalc.shares <= 0) {
		throw Errors.invalidAmount(`Minimum amount is $${MIN_USD_AMOUNT.toFixed(2)}`)
	}

	const estimatedShares = shareCalc.shares

	// =========================================================================
	// Build Signing Payload
	// =========================================================================

	// minShares provides slippage protection:
	// - Calculate expected shares at current price
	// - Apply slippage tolerance (e.g., 2% = user accepts 2% fewer shares)
	// - If execution price is worse than this, transaction reverts
	//
	// Example with $50 budget, 2% slippage, 100 expected shares:
	// - minShares = 100 * 0.98 = 98 shares
	// - If price moves and user would only get 97 shares, tx reverts
	// - If price moves and user gets 99 shares, tx succeeds
	const slippageMultiplier = 1 - slippageBps / 10000
	const minSharesHuman = estimatedShares * slippageMultiplier
	const minSharesScaled = toShareScale(minSharesHuman)

	const deadline = createDeadline(deadlineSeconds)
	const expiresAt = createDeadlineDate(deadlineSeconds)

	// Check if user needs to approve Permit2
	const needsPermit2Approval = permit2Allowance !== null && permit2Allowance < usdcAmountScaled

	// Generate Permit2 nonce (unordered, so any random value works)
	const permit2Nonce = generatePermit2Nonce()

	// Build Permit2 signing payload with BuyWitness
	// - permitted.amount = user's budget (max they authorize Permit2 to transfer)
	// - witness contains trade params that contract will verify
	const signingPayload = buildPermit2BuySigningPayload(chainId, {
		permitted: {
			token: usdcAddress,
			amount: usdcAmountScaled,
		},
		spender: market,
		nonce: permit2Nonce,
		deadline,
		witness: {
			bucket,
			usdcAmount: usdcAmountScaled,
			minShares: minSharesScaled,
			deadline,
		},
	})

	// =========================================================================
	// Build Quote (for display purposes)
	// =========================================================================

	const priceBeforeRaw = rawPrices[bucket]
	if (priceBeforeRaw === undefined) {
		throw Errors.invalidBucket(bucket, rawPrices.length)
	}
	const priceBefore = fromPriceScale(priceBeforeRaw)
	const cost = shareCalc.cost
	const fee = shareCalc.fee
	const total = shareCalc.total
	const avgPrice = cost / estimatedShares

	// Calculate accurate priceAfter using LMSR
	// getMarketInfo returns liquidityParam at index 1
	const [, liquidityParamBig] = marketInfo
	const SHARE_SCALE = 1e6
	const quantities = rawQuantities.map(q => Number(q) / SHARE_SCALE)
	const liquidityParam = Number(liquidityParamBig)
	const quantitiesAfter = [...quantities]
	quantitiesAfter[bucket] = (quantitiesAfter[bucket] ?? 0) + estimatedShares
	const priceAfter = computePrice(quantitiesAfter, liquidityParam, bucket)
	const priceImpact = priceBefore > 0 ? Math.max(0, (priceAfter - priceBefore) / priceBefore) : 0

	const quote: BuyQuote = {
		usdcIn: total,
		sharesOut: estimatedShares,
		cost,
		fee,
		total,
		avgPrice,
		priceImpact,
		priceBefore,
		priceAfter,
	}

	// Convert balance if included
	const userBalance = rawBalance !== null ? fromUsdcScale(rawBalance) : undefined

	return {
		type: "buy",
		signingPayload,
		quote,
		expiresAt,
		needsPermit2Approval,
		userBalance,
		chainId,
		_execution: {
			market,
			user,
			bucket,
			usdcAmount: usdcAmountScaled,
			minShares: minSharesScaled,
			deadline,
			usdcAddress,
			permit2Nonce,
		},
	}
}

/**
 * Encode approval transaction for Permit2
 *
 * Returns calldata for approving Permit2 to spend user's USDC.
 * This should be called once per user (can be done silently with embedded wallets).
 */
export function encodePermit2Approval(usdcAddress: Address): { to: Address; data: `0x${string}` } {
	const data = encodeFunctionData({
		abi: erc20Abi,
		functionName: "approve",
		args: [PERMIT2_ADDRESS, maxUint256],
	})
	return { to: usdcAddress, data }
}
